import { useQuery } from '@tanstack/react-query';
import { api } from '~/services/api';
import type { SessionData, AuthError } from './types';
import { loggerUtils } from '~/services/logger';

export interface UseSessionOptions {
  enabled?: boolean;
  refetchInterval?: number | false;
}

export function useSession(options?: UseSessionOptions) {
  return useQuery<SessionData, AuthError>({
    queryKey: ['session'],
    queryFn: async () => {
      try {
        loggerUtils.auth('token_refresh', { action: 'session_check' });

        const sessionData = await api.checkSession() as SessionData;

        // Store CSRF token if available
        if (sessionData.csrf_token) {
          (api as any).csrfToken = sessionData.csrf_token;
        }

        loggerUtils.auth('token_refresh', {
          success: sessionData.authenticated,
          reason: sessionData.authenticated ? 'authenticated' : 'not_authenticated'
        });

        return sessionData;
      } catch (error) {
        // Map API errors to AuthError type
        const authError: AuthError = {
          type: 'network',
          message: 'Failed to check session',
          recoverable: true,
        };

        if (error instanceof Error) {
          const status = (error as any).status;

          if (status === 401 || status === 403) {
            authError.type = 'auth';
            authError.message = 'Session expired';
            authError.recoverable = false;
            authError.status = status;
          } else if (status >= 500) {
            authError.type = 'server';
            authError.message = 'Server error';
            authError.recoverable = true;
            authError.status = status;
          } else if (error.message.includes('fetch') || error.message.includes('network')) {
            authError.type = 'network';
            authError.message = 'Network error';
            authError.recoverable = true;
          }
        }

        loggerUtils.auth('auth_error', {
          error: authError.message,
          type: authError.type
        });

        throw authError;
      }
    },
    staleTime: 30 * 1000,             // 30 seconds (more responsive to auth changes)
    gcTime: 5 * 60 * 1000,            // 5 minutes (garbage collection time)
    retry: (failureCount, error) => {
      // Don't retry auth failures (401, 403)
      if (error.type === 'auth') return false;

      // Retry network and server errors up to 3 times
      return failureCount < 3;
    },
    retryDelay: (attemptIndex) => {
      // Exponential backoff: 1s, 2s, 4s, capped at 30s
      return Math.min(1000 * 2 ** attemptIndex, 30000);
    },
    refetchOnWindowFocus: true,       // Recheck when user returns to tab
    refetchInterval: options?.refetchInterval ?? 5 * 60 * 1000, // Heartbeat every 5 min
    enabled: options?.enabled ?? true,
  });
}

// Helper hook to get just the user data
export function useUser() {
  const { data } = useSession();
  return data?.authenticated ? data.user : null;
}

// Helper hook to check if authenticated
export function useIsAuthenticated() {
  const { data, isLoading } = useSession();
  return {
    isAuthenticated: data?.authenticated ?? false,
    // Only use isLoading (initial load), not isFetching (background refetches)
    // Using isFetching causes login page to flash loading state on window focus
    isLoading,
  };
}
